import {
  Page,
  Content,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Card,
  CardContent,
  CardHeader,
} from '@WESCO-International/wdp-ui-components/components/card/Card';
import { Tab } from '@WESCO-International/wdp-ui-components/components/tab';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ClientDataTable } from '@WESCO-International/wdp-ui-components/components/table';
import { Grid } from '@material-ui/core';
import { Dropdown } from '@WESCO-International/wdp-ui-components/components/dropdown/Dropdown';
import Tag from '@WESCO-International/wdp-ui-components/components/tag/Tag';
import { EntityProvider } from '@backstage/plugin-catalog-react';
import {
  Direction,
  EntityRelationsGraph,
} from '@backstage/plugin-catalog-graph';
import { useRole } from '../../role/RoleContext';
import { useCatalogDetails } from './useCatalogDetails';
import { type CSSProperties } from 'react';

export const CatalogDetailsPage = () => {
  const { name } = useParams();
  const location = useLocation() as any;
  const item = location.state?.item;
  const displayName = item?.name || decodeURIComponent(name ?? '');
  const navigate = useNavigate();
  const { role } = useRole();
  const isAdmin = role === 'Admin';
  const stateKind = String(item?.kind ?? item?.entityKind ?? '').toLowerCase();
  const stateType = String(item?.type ?? '').toLowerCase();

  const detailsCardStyle: CSSProperties = {
    fontFamily: "'Source Sans Pro', sans-serif",
    fontSize: '0.875rem',
    lineHeight: 1.5,
    fontWeight: 600,
  };

  const {
    isApi,
    aboutName,
    aboutDescription,
    aboutOwner,
    aboutSystem,
    aboutType,
    aboutLifecycle,
    aboutDomain,
    aboutTagsArray,
    isLifecycleReady,
    readyLifecycleOptions,
    selectedLifecycle,
    handleLifecycleSelect,
    links,
    displayUrl,
    subComponentColumns,
    subComponentRows,
    componentsLoading,
    componentsError,
    activeComponentTab,
    componentTabs,
    activeComponentSection,
    handleComponentTabChange,
    activeApiTab,
    apiTabs,
    activeApiSection,
    handleApiTabChange,
    currentEntity,
    isUpdatingLifecycle,
    parentComponentName,
  } = useCatalogDetails({
    item,
    displayName,
    stateKind,
    stateType,
    isAdmin,
  });

  return (
    <Page themeId="home">
      <Content>
        <div className="pageHeader">
          <div className="wdp-heading s-5 semibold headingLarge">
            <div className="backTitleContainer">
              <a
                href="#"
                className="link backLink"
                aria-label="Back"
                onClick={e => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                ←
              </a>
              <span className="wdp-heading s-5 semibold headingLarge">
                {displayName}
              </span>
            </div>
          </div>
          {isAdmin && !isLifecycleReady && (
            <div
              className="flex"
              style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <span
                className="wdp-body s-4 semibold"
                style={{ color: '#111827' }}
              >
                Update Lifecycle
              </span>
              <Dropdown
                value={selectedLifecycle || aboutLifecycle || 'Ready to Use'}
                options={readyLifecycleOptions}
                emptyLabel="Select"
                onChange={handleLifecycleSelect}
                disabled={isUpdatingLifecycle}
                clearable={false}
                style={{ minWidth: 200 }}
              />
            </div>
          )}
        </div>

        <div className="contentWrap">
          <Grid container spacing={3} className="gridFullWidth">
            <Grid item xs={12} md={4} className="leftCol">
              <div className="columnStack leftStack">
                <Card
                  className="infoCardFixed aboutCard"
                  style={detailsCardStyle}
                >
                  <CardHeader
                    title={<span className="cardTitleSmall">About</span>}
                  />
                  <CardContent>
                    <div className="aboutSection">
                      <div className="aboutSectionHeader">
                        <div className="aboutSectionName">
                          {isApi ? 'API' : 'Component'}
                        </div>
                        <div className="aboutSectionKind">{aboutName}</div>
                      </div>

                      <div className="aboutSectionDivider" />

                      <div className="aboutSectionBlock">
                        <div className="aboutSectionLabel">Description</div>
                        <div className="aboutSectionValue">
                          {aboutDescription}
                        </div>
                      </div>

                      <div className="aboutSectionRow">
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">Owner</div>
                          <div className="aboutSectionValue">{aboutOwner}</div>
                        </div>
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">System</div>
                          <div className="aboutSectionValue">{aboutSystem}</div>
                        </div>
                      </div>

                      <div className="aboutSectionRow">
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">Type</div>
                          <div className="aboutSectionValue">{aboutType}</div>
                        </div>
                        <div className="aboutSectionCell aboutLifecycleCell">
                          <div className="aboutLifecycleLabel">
                            <div className="aboutSectionLabel">Lifecycle</div>
                          </div>
                          <div className="aboutSectionValue">
                            {aboutLifecycle}
                          </div>
                        </div>
                      </div>

                      <div className="aboutSectionRow">
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">Domain</div>
                          <div className="aboutSectionValue">{aboutDomain}</div>
                        </div>
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">
                            Parent Component
                          </div>
                          <div className="aboutSectionValue">
                            {parentComponentName}
                          </div>
                        </div>
                        <div className="aboutSectionCell">
                          <div className="aboutSectionLabel">Tags</div>
                          <div className="aboutSectionValue aboutSectionTags">
                            {aboutTagsArray.length === 0 ? (
                              <span>-</span>
                            ) : (
                              aboutTagsArray.map(tag => (
                                <Tag
                                  variant="info"
                                  key={tag}
                                  label={tag}
                                  size="medium"
                                  radius="xl"
                                />
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="infoCardFixed linksCard"
                  style={detailsCardStyle}
                >
                  <CardHeader
                    title={<span className="cardTitleSmall">Links</span>}
                  />
                  <CardContent>
                    <div className="linksText">
                      {links.length > 0 ? (
                        <ul className="linksList">
                          {links.map((l, i) => (
                            <li key={`${l.label}-${i}`}>
                              {i > 0 && <div className="linkDivider" />}
                              <a
                                className="linkTitle linkTitleClickable"
                                href={l.url}
                                target="_blank"
                                rel="noreferrer"
                                title={l.url}
                              >
                                {l.label || displayUrl(l.url)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="emptyState">No data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </Grid>

            <Grid item xs={12} md={8} className="rightCol">
              {!isApi && (
                <div className="columnStack rightStack">
                  <Card
                    className={`infoCardFixed dataCard relationshipsCard subComponentsCard ${
                      (subComponentRows ?? []).length === 0 ? 'compactCard' : ''
                    }`}
                    style={
                      (subComponentRows ?? []).length === 0
                        ? { minHeight: 'auto' }
                        : undefined
                    }
                  >
                    <CardHeader
                      title={
                        <span className="cardTitleSmall">Relationships</span>
                      }
                    />
                    <CardContent>
                      <div style={{ marginBottom: 8 }}>
                        <Tab
                          data={componentTabs}
                          active={activeComponentTab}
                          onChange={handleComponentTabChange}
                          position="left"
                          size="normal"
                          type="boxed"
                          fullwidth={false}
                        />
                      </div>

                      <div className="tableText">
                        {activeComponentSection?.showLoading &&
                        componentsLoading ? (
                          <Progress />
                        ) : (
                          <></>
                        )}
                        {activeComponentSection?.showLoading &&
                        componentsError ? (
                          <ResponseErrorPanel error={componentsError} />
                        ) : (
                          <></>
                        )}
                        {!activeComponentSection?.showLoading ||
                        (!componentsLoading && !componentsError) ? (
                          (activeComponentSection?.rows ?? []).length > 0 ? (
                            <ClientDataTable
                              columns={activeComponentSection?.columns}
                              rows={activeComponentSection?.rows}
                              paginate
                              resize={true}
                              dense
                              rowHeight={42}
                              headerRowHeight={42}
                              verticalBorder={false}
                            />
                          ) : (
                            <div className="emptyState">No data available</div>
                          )
                        ) : (
                          <></>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="infoCardFixed dataCard relationshipsCard">
                    <CardHeader
                      title={
                        <span className="cardTitleSmall">Sub-Components</span>
                      }
                    />
                    <CardContent>
                      <div className="tableText">
                        {componentsLoading ? <Progress /> : <></>}
                        {componentsError ? (
                          <ResponseErrorPanel error={componentsError} />
                        ) : (
                          <></>
                        )}
                        {!componentsLoading && !componentsError ? (
                          (subComponentRows ?? []).length > 0 ? (
                            <ClientDataTable
                              columns={subComponentColumns}
                              rows={subComponentRows}
                              paginate
                              resize={true}
                              dense
                              rowHeight={42}
                              headerRowHeight={42}
                              verticalBorder={false}
                            />
                          ) : (
                            <div className="emptyState">No data available</div>
                          )
                        ) : (
                          <></>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="infoCardFixed dataCard relationshipsCard">
                    <CardHeader
                      title={
                        <span className="cardTitleSmall">
                          Relationship Visualization
                        </span>
                      }
                    />
                    <CardContent>
                      {currentEntity ? (
                        <div className="graphCard" style={{ height: 400 }}>
                          <EntityProvider entity={currentEntity}>
                            <EntityRelationsGraph
                              rootEntityNames={[
                                {
                                  kind: currentEntity?.kind ?? 'Component',
                                  namespace:
                                    currentEntity?.metadata?.namespace ??
                                    'default',
                                  name:
                                    currentEntity?.metadata?.name ??
                                    displayName,
                                },
                              ]}
                              direction={Direction.LEFT_RIGHT}
                              zoom="enable-on-click"
                              allowFullscreen
                            />
                          </EntityProvider>
                        </div>
                      ) : (
                        <div className="emptyState">No data available</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {isApi && (
                <div className="columnStack rightStack">
                  <Card className="infoCardFixed dataCard relationshipsCard">
                    <CardHeader
                      title={
                        <span className="cardTitleSmall">Relationships</span>
                      }
                    />
                    <CardContent>
                      <div style={{ marginBottom: 8 }}>
                        <Tab
                          data={apiTabs}
                          active={activeApiTab}
                          onChange={handleApiTabChange}
                          position="left"
                          size="normal"
                          type="boxed"
                          fullwidth={false}
                        />
                      </div>

                      <div className="tableText">
                        {componentsLoading ? <Progress /> : <></>}
                        {componentsError ? (
                          <ResponseErrorPanel error={componentsError} />
                        ) : (
                          <></>
                        )}
                        {!componentsLoading && !componentsError ? (
                          (activeApiSection?.rows ?? []).length > 0 ? (
                            <ClientDataTable
                              columns={activeApiSection?.columns}
                              rows={activeApiSection?.rows}
                              paginate
                              resize={true}
                              dense
                              rowHeight={42}
                              headerRowHeight={42}
                              verticalBorder={false}
                            />
                          ) : (
                            <div className="emptyState">No data available</div>
                          )
                        ) : (
                          <></>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="infoCardFixed dataCard relationshipsCard">
                    <CardHeader
                      title={
                        <span className="cardTitleSmall">
                          Relationship Visualization
                        </span>
                      }
                    />
                    <CardContent>
                      {currentEntity ? (
                        <div className="graphCard" style={{ height: 400 }}>
                          <EntityProvider entity={currentEntity}>
                            <EntityRelationsGraph
                              rootEntityNames={[
                                {
                                  kind: currentEntity?.kind ?? 'API',
                                  namespace:
                                    currentEntity?.metadata?.namespace ??
                                    'default',
                                  name:
                                    currentEntity?.metadata?.name ??
                                    displayName,
                                },
                              ]}
                              direction={Direction.LEFT_RIGHT}
                              zoom="enable-on-click"
                              allowFullscreen
                            />
                          </EntityProvider>
                        </div>
                      ) : (
                        <div className="emptyState">No data available</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Component sections above include APIs Provided/Consumed with ordering */}
            </Grid>
          </Grid>
        </div>
      </Content>
    </Page>
  );
};
